import app from 'app';
import _ from 'lodash';
import moment from 'moment';
import { parseName } from 'helpers/stringParsers';
import claimTemplateUrl from './claim.html';
import claimNoPhiTemplateUrl from './claimNoPhi.html';
import claimAlertsTemplateUrl from './claimAlerts.html';
import claimAmountsTemplateUrl from './claimAmounts.html';
import claimButtonsTemplateUrl from './claimButtons.html';
import claimLineItemsTemplateUrl from './claimLineItems.html';
import mxPopover from 'components/tooltips/mxPopover/mxPopover';
import { getPage, logEvent, eventCategories } from 'helpers/analytics';

import './claimValWithRemark';
import './claim.scss';
import './claimHeader.scss';
import './claimLines.scss';

import getHelpCenterRoute from 'helpers/helpCenterRoutes';
import { isOverAge } from 'helpers/age';

import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-circle-check';
import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-circle-x';
import '@collectivehealth/dls-icons/dist/es/elements/dls-icon-download';

function ClaimCtrl($filter, $log, Claims, matchmedia, openViaAjax) {
  const ClaimCtrl = this;

  ClaimCtrl.getHelpCenterRoute = getHelpCenterRoute;

  function getNoPhiStatus() {
    const is13OrOver = isOverAge(ClaimCtrl.claim.patient.dateOfBirth, 13);

    if (!is13OrOver && ClaimCtrl.claim.phiLevel === 'NO_PHI') {
      // For outliers where the child is under 13 but one parent has
      // removed PHI permissions from the other.
      return 'UNDER13_NO_PHI';
    }

    if (!ClaimCtrl.isRegistered) {
      return 'CAN_INVITE';
    }

    return 'NO_PHI';
  }

  ClaimCtrl.onHelpCenterDeepLinkClick = () => {
    logEvent('deepLinkClicked', {
      category: eventCategories.HELP,
      label: 'So what is it?',
      page: getPage()
    });
  };

  ClaimCtrl.$onInit = () => {
    ClaimCtrl.totalPopoverOptions = {
      component: mxPopover,
      locals: {
        content:
          'After you get care, we calculate how your benefits applied to your visit. ' +
          'Make sure the total of “Your responsibility” matches what your provider charges you. ' +
          'If they bill you a different amount, contact us so we can&nbsp;help.'
      },
      constraints: [
        {
          to: 'scrollParent',
          attachment: 'both'
        }
      ],
      attachment: 'top right',
      targetAttachment: 'bottom right',
      offset: '-4px 0'
    };
    // exposing template URLs to template for ng-includes
    ClaimCtrl.claimNoPhiTemplateUrl = claimNoPhiTemplateUrl;
    ClaimCtrl.claimAlertsTemplateUrl = claimAlertsTemplateUrl;
    ClaimCtrl.claimAmountsTemplateUrl = claimAmountsTemplateUrl;
    ClaimCtrl.claimButtonsTemplateUrl = claimButtonsTemplateUrl;
    ClaimCtrl.claimLineItemsTemplateUrl = claimLineItemsTemplateUrl;

    ClaimCtrl.expandRow = -1;
    ClaimCtrl.showDetails =
      ClaimCtrl.claim.phiLevel === 'FULL_PHI' ||
      ClaimCtrl.claim.phiLevel === 'LIMITED_PHI';

    ClaimCtrl.patientName = parseName(ClaimCtrl.claim.patient);

    ClaimCtrl.noPhiStatus = getNoPhiStatus();

    /**
     * A claim number is in the 'claimSystemId' field on medical claims and in the
     * 'networkClaimId' field on dental claims.
     *
     * TODO: API should use one field for claim numbers.
     */
    ClaimCtrl.claimNumber =
      ClaimCtrl.claim.claimSystemId || ClaimCtrl.claim.networkClaimId;

    // NOTE: Sometimes in denied claims there is a chance of not having an associated
    // planMembership. In that case the memberId is not displayed.
    if (
      ClaimCtrl.claim.planType === 'medical' &&
      ClaimCtrl.claim.planMembership
    ) {
      ClaimCtrl.actualMemberId = [
        ClaimCtrl.claim.planMembership.memberIdPrefix,
        ClaimCtrl.claim.planMembership.memberId
      ].join('');
    }

    // End date is always present, so only show it if it differs by start date by more than 1 day.
    ClaimCtrl.showEndDate =
      moment(ClaimCtrl.claim.dateOfServiceThru).diff(
        ClaimCtrl.claim.dateOfServiceStart,
        'days'
      ) > 1;
  };

  /**
   * Opens a new window with a PDF EOB document.
   */
  ClaimCtrl.download = function() {
    logEvent('printMBSDownloaded', {
      category: eventCategories.ACTIVITY,
      planType: ClaimCtrl.claim.planType
    });
    openViaAjax(Claims.getEobLink(ClaimCtrl.claim.id));
  };

  /**
   * Builds a string describing how the patient responsibility for a claim line
   * item was determined.
   * @param  {object} claimLine - Item from a claim's claimLines list.
   * @return {string}           - String describing patient responsibility.
   */
  ClaimCtrl.buildCostExplanation = function(claimLine) {
    var costs = [],
      check = 0;

    // Shorthand for currency filter.
    var cost = function(c) {
      return $filter('currency')(c, '$', 2);
    };

    // If the line item has a co-pay, deductible/coinsurance is irrelevant.
    if (_.get(claimLine, 'patientResponsibilityExplanation.copay') > 0) {
      costs.push('Copay');
      check += claimLine.patientResponsibilityExplanation.copay;
    } else {
      // Add deductible amount, if present.
      if (_.get(claimLine, 'patientResponsibilityExplanation.deductible') > 0) {
        costs.push(
          cost(claimLine.patientResponsibilityExplanation.deductible) +
            ' deductible'
        );
        check += claimLine.patientResponsibilityExplanation.deductible;
      }

      // Add coinsurance amount and percent, if present.
      if (
        _.get(
          claimLine,
          'patientResponsibilityExplanation.coinsurance.amount'
        ) > 0
      ) {
        costs.push(
          cost(claimLine.patientResponsibilityExplanation.coinsurance.amount) +
            ' (' +
            claimLine.patientResponsibilityExplanation.coinsurance.percent +
            '% up to your out-of-pocket-max)'
        );
        check += claimLine.patientResponsibilityExplanation.coinsurance.amount;
      }

      // Add exclusions, if present.
      if (_.get(claimLine, 'patientResponsibilityExplanation.notCovered') > 0) {
        costs.push(
          cost(claimLine.patientResponsibilityExplanation.notCovered) +
            ' not covered by your plan.'
        );
        check += claimLine.patientResponsibilityExplanation.notCovered;
      }
    }

    if (claimLine.patientResponsibility !== check) {
      $log.warn(
        'Patient responsibility: ',
        claimLine.patientResponsibility,
        ' did not match check:',
        check
      );
    }

    return costs.join(' + ');
  };

  /**
   * Expands or collapses the row at the given index.
   * @param  {string|number} index - Index of the row to toggle.
   */
  ClaimCtrl.toggleRow = function(index) {
    ClaimCtrl.expandRow =
      ClaimCtrl.expandRow === index || !ClaimCtrl.isPhoneOrTablet()
        ? -1
        : index;
  };

  /**
   * @return {boolean} - True if we are on tablet and not on desktop.
   */
  ClaimCtrl.isPhoneOrTablet = function() {
    return (
      matchmedia.isPhone() || (matchmedia.isTablet() && !matchmedia.isDesktop())
    );
  };

  ClaimCtrl.hasHRAAmount = function() {
    return ClaimCtrl.claim.totalIntegratedHraAmount > 0;
  };

  // Collapse expanded rows on desktop.
  matchmedia.onDesktop(function(mql) {
    if (mql.matches) {
      ClaimCtrl.expandRow = -1;
    }
  });
}

app.component('claim', {
  bindings: {
    claim: '<',
    isRegistered: '<'
  },
  controller: ClaimCtrl,
  controllerAs: 'ClaimCtrl',
  templateUrl: claimTemplateUrl
});

export default 'claim';
